import '../style/index.scss';
import BaseLayout from '@layouts/base-layout';
import { wrapper } from '@redux/store';
import { Socket } from 'src/socket';
import { ThemeProvider } from 'next-themes';
// eslint-disable-next-line camelcase
import { Merriweather_Sans } from 'next/font/google';
import { GoogleAnalytics, event } from 'nextjs-google-analytics';
import { ConfigProvider } from 'antd';
import { AppProps } from 'next/app';
import { useEffect, useMemo } from 'react';
import { StyleProvider, createCache } from '@ant-design/cssinjs';
import GlobalErrorHandler from 'src/components/common/ErrorBoundary';
import { APIRequest } from '@services/api-request';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

const myFont = Merriweather_Sans({ subsets: ['latin'] });

function Application({ Component, pageProps }: AppProps) {
  const { layout, authenticate, noredirect } = (Component as any);
  const gaCode = useSelector((state: any) => state.settings?.gaCode);
  const router = useRouter();

  // Create stable cache for StyleProvider
  const cache = useMemo(() => createCache(), []);

  // Track route changes
  useEffect(() => {
    if (!gaCode) return;

    const handleRouteChange = (url: string) => {
      event('page_view', {
        page_path: url,
        page_title: document.title
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, gaCode]);

  useEffect(() => {
    // Set API endpoint for client-side requests
    if (!APIRequest.API_ENDPOINT) {
      APIRequest.API_ENDPOINT = process.env.NEXT_PUBLIC_API_ENDPOINT || process.env.API_ENDPOINT || 'https://api.talentiq.studio';
    }

    // Add global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      event.preventDefault();
    };

    const handleError = (event) => {
      console.error('Global error:', event.error);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <GlobalErrorHandler>
      {gaCode && <GoogleAnalytics trackPageViews gaMeasurementId={gaCode} />}
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        <meta name="robots" content="index,follow" />
        <link rel="dns-prefetch" href="//api.talentiq.studio" />
        <link rel="preconnect" href="https://api.talentiq.studio" crossOrigin="anonymous" />
        <script type="text/javascript" src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" async />
      </Head>
      <StyleProvider cache={cache} hashPriority="high">
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#42b1e8',
              colorInfo: '#42b1e8',
              colorLink: '#42b1e8',
              colorSuccess: '#4ec52a'
            },
            hashed: false
          }}
          virtual={false}
        >
          <ThemeProvider enableSystem={false} themes={['light', 'dark']} defaultTheme="light">
            <Socket>
              <main className={myFont.className}>
                <BaseLayout layout={layout} authenticate={authenticate} noredirect={noredirect}>
                  <Component {...pageProps} />
                </BaseLayout>
              </main>
            </Socket>
          </ThemeProvider>
        </ConfigProvider>
      </StyleProvider>
    </GlobalErrorHandler>
  );
}

export default wrapper.withRedux(Application);
